import { url, tokenRef } from "api";
import store from "store";
import { issueFinished, issueReopened, rowSettled } from "actions/issues";
import { isFinishedStatus } from "helpers/issueListPrune";

// Session storage keys
const GUEST_TOKEN_KEY = "issue_guest_token";

// ==================== TOKEN MANAGEMENT ====================
export const setGuestToken = (token) => {
  sessionStorage.setItem(GUEST_TOKEN_KEY, token);
};

export const getGuestToken = () => {
  return sessionStorage.getItem(GUEST_TOKEN_KEY);
};

export const clearGuestToken = () => {
  sessionStorage.removeItem(GUEST_TOKEN_KEY);
};

// ==================== HELPERS ====================
const getHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  const guestToken = getGuestToken();
  const userToken = localStorage.getItem(tokenRef);

  if (guestToken) {
    headers["x-guest-token"] = guestToken;
  }
  if (userToken) {
    headers["Authorization"] = `Bearer ${userToken}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.err || "Request failed");
  }
  return data;
};

// ==================== ISSUE CRUD ====================

/**
 * Create a new issue
 */
export const createIssue = async (issueData) => {
  const response = await fetch(`${url}/issue`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(issueData),
  });
  return handleResponse(response);
};

/**
 * Get all issues for the current company (paginated)
 */
/** ?areaSignOff= &areaStage= &areaSignOffAsOf= onto a query, only with a
 *  project (the server refuses the filter without one). Shared by the list,
 *  the bulk tools and every export, so all of them carry the same filter. */
export const appendAreaSignOff = (qs, { projectId, areaSignOff, areaStage, areaSignOffAsOf, ownerSignOff } = {}) => {
  // Signed off by owner/GC (owner, 2026-10-21): signed / planned / not.
  // Project only, like the area sign-off - and carried by the same callers.
  if (projectId && ownerSignOff) qs.append("ownerSignOff", ownerSignOff);
  if (!projectId || !areaSignOff) return;
  qs.append("areaSignOff", areaSignOff);
  if (areaStage) qs.append("areaStage", areaStage);
  if (areaSignOffAsOf) qs.append("areaSignOffAsOf", areaSignOffAsOf);
};

export const getIssues = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.index !== undefined) queryParams.append("index", params.index);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.sort) queryParams.append("sort", params.sort);
  if (params.sort_dir) queryParams.append("sort_dir", params.sort_dir);
  if (params.search) queryParams.append("search", params.search);
  if (params.column) queryParams.append("column", params.column);
  if (params.status) queryParams.append("status", params.status);
  if (params.priority) queryParams.append("priority", params.priority);
  if (params.projectId) queryParams.append("projectId", params.projectId);
  if (params.needsTriage !== undefined) queryParams.append("needsTriage", params.needsTriage);
  if (params.dueToday) queryParams.append("dueToday", "true");
  // Was never forwarded, so the toolbar's "Created today" button set state
  // the server never saw and the list came back unfiltered.
  if (params.createdToday) queryParams.append("createdToday", "true");
  if (params.overdue) queryParams.append("overdue", "true");
  // WHOSE today. The server runs UTC; the reader does not. Without this
  // the day boundary was the server's, so an issue raised in the evening
  // in the Americas was already "tomorrow" to the filter that was meant
  // to show it. Sent only when a day filter is actually on.
  // Inclusive day ranges, 'YYYY-MM-DD' — the windowed form of the three
  // buttons above. Either end may be sent on its own.
  if (params.createdFrom) queryParams.append("createdFrom", params.createdFrom);
  if (params.createdTo) queryParams.append("createdTo", params.createdTo);
  if (params.dueFrom) queryParams.append("dueFrom", params.dueFrom);
  if (params.dueTo) queryParams.append("dueTo", params.dueTo);
  if (
    params.dueToday || params.createdToday || params.overdue ||
    params.createdFrom || params.createdTo
  ) {
    queryParams.append("tzOffset", String(new Date().getTimezoneOffset()));
  }
  if (params.assignee) queryParams.append("assignee", params.assignee);
  if (params.ganttItemId) queryParams.append("ganttItemId", params.ganttItemId);
  // Toolbar checkboxes, on by default: drop abandoned / resolved (and
  // closed) issues from the list. Ignored server-side when an explicit
  // status filter is set.
  if (params.hideAbandoned) queryParams.append("hideAbandoned", "true");
  if (params.hideResolved) queryParams.append("hideResolved", "true");
  if (params.hideUnderReview) queryParams.append("hideUnderReview", "true");
  if (params.hidePending) queryParams.append("hidePending", "true");
  // "false" lists the issues not yet approved to resolve (Review Center's
  // Approve to resolve tab), "true" the approved ones.
  if (params.approvedToResolve !== undefined) queryParams.append("approvedToResolve", String(params.approvedToResolve));
  // ?slim=true - for callers asking for hundreds of rows at once (pickers,
  // review queues, the duplicate finder). Every field of the ISSUE is still
  // there; the joined project, last editor and responsible people are cut
  // to what a row renders. Without it the whole project document rides on
  // every row - a megabyte each once the project carries a model.
  if (params.slim) queryParams.append("slim", "true");
  // The raiser's own label ("SNAG-Duct clash"). Exact, so a filtered list
  // and a filtered export agree on what the batch is.
  if (params.titleTag) queryParams.append("titleTag", params.titleTag);
  // Who raised it / who touched it last. `updatedBy` takes a user id or
  // "guest:<id>" for somebody working through a shared link.
  if (params.createdBy) queryParams.append("createdBy", params.createdBy);
  if (params.updatedBy) queryParams.append("updatedBy", params.updatedBy);
  // THE CONTRACTOR FILTER NEVER LEFT THE BROWSER. The toolbar held it, the
  // export sent it (exportIssues below) and the list route honours it - but
  // this builder had no line for it, so the list showed every contractor's
  // issues while the export of the same screen showed one. Found 2026-10-15
  // making the bulk tools act on "the list as the screen shows it".
  // Only with a project: the server refuses a contractor filter without one.
  if (params.contractor && params.contractor !== "all" && params.projectId) queryParams.append("contractor", params.contractor);
  // Area sign-off (owner, 2026-10-15): is the issue's area signed off by the
  // schedule's activity sign-offs. Project only, like the contractor.
  appendAreaSignOff(queryParams, params);
  // Level / area / grid / system / discipline (owner, 2026-10-17), each "A,B".
  for (const k of ["level", "area", "grid", "system", "discipline"]) if (params[k]) queryParams.append(k, params[k]);

  const response = await fetch(`${url}/issue?${queryParams.toString()}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get issue by ID (authenticated users)
 */
export const getIssueById = async (issueId) => {
  const response = await fetch(`${url}/issue/id/${issueId}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get auto-detected candidate fix elements for an issue (Bolt-on 2).
 *
 * Server diffs the issue's frozen openedAtSnapshot against the latest
 * commit's CommitElement rows and returns { added, removed, modified,
 * affectedTiles, ... }. The IssueResolutionPage calls this on mount
 * to pre-populate the element picker and to hand the viewer the
 * affectedTiles hint (isolate + prefetch).
 *
 * Returns null on error — callers fall back to today's manual picker.
 * An empty response (reason=no-snapshot / no-commits) is a valid,
 * non-null result the caller treats the same way.
 */
export const getCandidateFixElements = async (issueId) => {
  try {
    const response = await fetch(
      `${url}/issue/${encodeURIComponent(issueId)}/candidate-fix-elements`,
      { method: "GET", headers: getHeaders() }
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (e) {
    console.warn("getCandidateFixElements failed:", e?.message);
    return null;
  }
};

/**
 * Get issue by share token (public access)
 */
export const getIssueByShareToken = async (shareToken) => {
  const response = await fetch(`${url}/issue/share/${shareToken}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Update issue
 */
// The estimate on its own (PUT /issue/:id/estimate): for whoever holds
// estimateEdit and may see the issue, even without edit rights on it.
export const setIssueEstimate = async (issueId, estimatedHours) => {
  const response = await fetch(`${url}/issue/${issueId}/estimate`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ estimatedHours }),
  });
  return handleResponse(response);
};

export const updateIssue = async (issueId, updateData) => {
  const response = await fetch(`${url}/issue/${issueId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(updateData),
  });
  return handleResponse(response);
};

// "Resend deliverables" to one of the issue's deliverable recipients: mails
// that address now with the deliverables attached. Returns { sent }.
export const resendIssueDeliverable = async (issueId, email) => {
  const response = await fetch(`${url}/issue/${issueId}/deliverable/resend`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ email }),
  });
  return handleResponse(response);
};

// UPLOAD THE DELIVERABLES TO PROCORE / ACC (owner, 2026-11-04), beside
// Resend. The answer has the same shape as the shop drawings route's, so the
// popup's boxes are built by the same boxesOf.
export const getIssueDeliverableTargets = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/deliverable/external-targets`, {
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// `destinations`: destination ids (several per platform); `providers`: each platform's first
export const uploadIssueDeliverables = async (issueId, providers, destinations) => {
  const response = await fetch(`${url}/issue/${issueId}/deliverable/external`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(destinations ? { providers, destinations } : { providers }),
  });
  return handleResponse(response);
};

// DOWNLOAD THE DELIVERABLES LOCALLY (owner, 2026-11-04). The file comes back
// as a short-lived link the browser downloads natively rather than as bytes
// through fetch()+blob(): a 20 MB bundle pulled that way surfaced every
// network break as a bare "Failed to fetch" with nothing to retry.
export const downloadIssueDeliverables = async (issueId, mode) => {
  const response = await fetch(`${url}/issue/${issueId}/deliverable/download`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ mode }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.link) throw new Error(data.err || "Could not build the download");
  const a = document.createElement("a");
  a.href = `${url}${data.link}`;
  a.download = data.filename || `deliverables.${mode === "combined" ? "pdf" : "zip"}`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return {
    count: Number(data.count || 0), included: Number(data.included || 0),
    missing: Number(data.missing || 0), missingNames: data.missingNames || "",
  };
};

/**
 * Issues whose schedule activity is gone, grouped by the activity they
 * used to hang off.
 *
 * Grouped rather than flat because that is the shape of the job: a
 * schedule rebuild moves the pile that was on "Level 1 Area A" onto the
 * NEW "Level 1 Area A", not all 208 issues onto one row.
 */
export const getOrphanedIssues = async (projectId, cap) => {
  const qs = cap ? `&cap=${encodeURIComponent(cap)}` : "";
  const response = await fetch(`${url}/issue/orphaned?projectId=${encodeURIComponent(projectId)}${qs}`, {
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Move many issues onto one schedule activity.
 *
 * The reply separates `moved` from `alreadyThere` and NAMES anything
 * skipped for being on another project, so the screen can say what it
 * did rather than "done".
 */
// fromActivityId: the activity being replaced on an issue that is on several
// (the orphaned view sends the gone one, so the others stay).
export const bulkReassignIssues = async (issueIds, ganttItemId, fromActivityId) => {
  const response = await fetch(`${url}/issue/bulk-reassign`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ issueIds, ganttItemId, ...(fromActivityId ? { fromActivityId } : {}) }),
  });
  return handleResponse(response);
};

/**
 * Delete issue (soft delete)
 */
export const deleteIssue = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}`, {
    method: "DELETE",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ==================== GUEST OPERATIONS ====================

/**
 * Register as a guest user
 */
export const registerGuest = async (shareToken, guestInfo) => {
  const response = await fetch(`${url}/issue/guest/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      shareToken,
      ...guestInfo,
    }),
  });
  
  const data = await handleResponse(response);
  
  // Store guest token in session
  if (data.token) {
    setGuestToken(data.token);
  }
  
  return data;
};

/**
 * Save markup: annotated raster (data URL) → new versioned image on the
 * server + vector objects, tracks who saved it. Works for users and
 * magic-link/guest editors (headers carry whichever token exists).
 */
export const saveIssueMarkup = async (issueId, { imageData, issueObjects, canvasState, note } = {}) => {
  const response = await fetch(`${url}/issue/${issueId}/markup`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ imageData, issueObjects, canvasState, note }),
  });
  return handleResponse(response);
};

// ==================== SOURCE SHEET + MEDIA ATTACHMENTS ====================
// Multipart helpers: no Content-Type header (the browser sets the boundary).
const authOnlyHeaders = () => {
  const h = getHeaders();
  delete h["Content-Type"];
  return h;
};

/** Title tags in use, commonest first → [{ tag, count }]. Fills the list's
 *  Tag filter and the create pages' autocomplete, so the set on offer is
 *  exactly the set that returns rows. */
export const getIssueTags = async (projectId) => {
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  const response = await fetch(`${url}/issue/tags${q}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** Who has raised or last touched an issue here → { createdBy: [], updatedBy: [] },
 *  each { id, name, email, kind, count }. `kind` is "user" for an account and
 *  "guest" for somebody working through a shared link; a guest's id comes
 *  back as "guest:<id>", which is what the updatedBy filter takes. */

/** The project's issues by area sign-off for one stage and day →
 *  { stage, stageLabel, asOf, counts: { signed, open, none }, areas: [{ name, issues, levels: [{ level, signed, signedOffAt }] }] } */
export const getAreaSignOff = async (projectId, { areaStage, areaSignOffAsOf, areaSignOffSource } = {}) => {
  const qs = new URLSearchParams({ projectId });
  if (areaStage) qs.append("areaStage", areaStage);
  if (areaSignOffAsOf) qs.append("areaSignOffAsOf", areaSignOffAsOf);
  if (areaSignOffSource) qs.append("areaSignOffSource", areaSignOffSource);
  const response = await fetch(`${url}/issue/area-sign-off?${qs}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** What the Level / Area / Grid / System / Discipline filters offer →
 *  { level: { values: [{ value, count }], none }, area, grid, system, discipline }.
 *  Without a project, across every issue the caller can see. */
export const getIssueFilterOptions = async (projectId) => {
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  const response = await fetch(`${url}/issue/filter-options${q}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

export const getIssuePeople = async (projectId) => {
  const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  const response = await fetch(`${url}/issue/people${q}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** Doc-control PDF sheets of the issue's project → { sheets, current } */
export const getIssueSheets = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/sheets`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** Duplicate handling: the issues on this project that a duplicate can be
 *  merged INTO - every one of them, newest first, with the thumbnail, number
 *  and description each row shows. `search` is optional: with no term this is
 *  the full list, which is what the picker opens on.
 *
 *  Resolved and abandoned issues are left out because the merge route refuses
 *  them ("merge into an open issue"), so offering one would only be a dead
 *  choice; the current issue is left out by `excludeId`. */
export const searchIssuesForMerge = async (projectId, search, excludeId, limit = 500) => {
  const q = new URLSearchParams();
  q.append("projectId", projectId);
  if (search) q.append("search", search);
  if (excludeId) q.append("excludeId", excludeId);
  q.append("limit", String(limit));
  q.append("index", "0");
  q.append("sort", "displayId");
  q.append("sort_dir", "-1");
  q.append("hideAbandoned", "true");
  q.append("hideResolved", "true");
  // Without this the joined project comes back WHOLE on every row - over a
  // megabyte each on a project with a model, so 68 candidates arrived as
  // 86 MB. Slim trims the joins to the fields a row renders.
  q.append("slim", "true");
  const response = await fetch(`${url}/issue?${q.toString()}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** Merge this issue (the duplicate) into `targetId` (the one that survives):
 *  people, comments, files, images and attributes all move onto the target;
 *  this issue is abandoned with mergedInto stamped. */
export const mergeIssueInto = async (issueId, targetId) => {
  const response = await fetch(`${url}/issue/${issueId}/merge`, {
    method: "PUT", headers: getHeaders(), body: JSON.stringify({ targetId }),
  });
  const body = await handleResponse(response);
  announceStatus(issueId, "abandoned");   // the duplicate leaves the lists
  return body;
};

/** Move this issue to another project: it takes that project, its prefix
 *  and the next number there; the response carries the new label. */
export const moveIssueToProject = async (issueId, projectId) => {
  const response = await fetch(`${url}/issue/${issueId}/move`, {
    method: "PUT", headers: getHeaders(), body: JSON.stringify({ projectId }),
  });
  return handleResponse(response);
};

/** Point the issue's 2D source at a doc-control sheet (docControlId) or detach it (null). */
export const setIssueSheet = async (issueId, docControlId) => {
  const response = await fetch(`${url}/issue/${issueId}/sheet`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(docControlId ? { docControlId } : { clear: true }),
  });
  return handleResponse(response);
};

/** Import a one-off PDF / image file as the issue's 2D sheet. */
export const uploadIssueSheet = async (issueId, file, { sheetNumber = "" } = {}) => {
  const fd = new FormData();
  fd.append("file", file, file.name || "sheet");
  if (sheetNumber) fd.append("sheetNumber", sheetNumber);
  const response = await fetch(`${url}/issue/${issueId}/sheet/upload`, { method: "POST", headers: authOnlyHeaders(), body: fd });
  return handleResponse(response);
};

/** Attach media files (image / video / PDF / Excel / CSV) → { added, attachments } */
export const uploadIssueAttachments = async (issueId, files, { role = "", keepPicture = false } = {}) => {
  const fd = new FormData();
  // keepPicture: an image among the files stays an attachment and does not
  // become the issue's picture.
  if (keepPicture) fd.append("promote", "0");
  // role "resolution": files are resolution evidence (Mark-resolved dialog) -
  // filed in Document Control as "Resolution screenshot / document".
  if (role) fd.append("role", role);
  [...files].forEach((f, i) => fd.append("files", f, f.name || `pasted-${i + 1}`));
  const response = await fetch(`${url}/issue/${issueId}/attachments`, { method: "POST", headers: authOnlyHeaders(), body: fd });
  return handleResponse(response);
};

/** Remove one image of the media history (a version, or the picture the issue was raised with). */
export const deleteIssueImage = async (issueId, imageUrl) => {
  const response = await fetch(`${url}/issue/${issueId}/images?url=${encodeURIComponent(imageUrl)}`, { method: "DELETE", headers: getHeaders() });
  return handleResponse(response);
};

/** Drop one sheet from the issue's roster (the active one hands over to the next). */
export const deleteIssueSheet = async (issueId, sheetUrl) => {
  const response = await fetch(`${url}/issue/${issueId}/sheets?url=${encodeURIComponent(sheetUrl)}`, { method: "DELETE", headers: getHeaders() });
  return handleResponse(response);
};

export const deleteIssueAttachment = async (issueId, attachmentId) => {
  const response = await fetch(`${url}/issue/${issueId}/attachments/${attachmentId}`, { method: "DELETE", headers: getHeaders() });
  return handleResponse(response);
};

/** Email a please-complete reminder to the assignee / responsibles → { sent, recipients } */
// Progress to completion, as the person doing the work reports it.
// Guest-capable (getHeaders sends the magic-link token), because the
// people who know the number are usually the partner's, not the owner's.
export const updateIssueProgress = async (issueId, progress) => {
  const response = await fetch(`${url}/issue/${issueId}/progress`, {
    method: "PUT", headers: getHeaders(), body: JSON.stringify({ progress }),
  });
  return handleResponse(response);
};

// The assignee handing the issue back: whatever they changed is already
// saved, this announces it. The canvas image is posted through
// saveIssueMarkup first, so the mail and the thumbnail show what they
// were looking at when they pressed Submit.
export const submitIssue = async (issueId, body = {}) => {
  const response = await fetch(`${url}/issue/${issueId}/submit`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body),
  });
  return handleResponse(response);
};

export const remindIssue = async (issueId, emails = []) => {
  const response = await fetch(`${url}/issue/${issueId}/remind`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(emails.length ? { emails } : {}),
  });
  return handleResponse(response);
};

// ==================== CHANGE TRACKING ====================

/**
 * Add a change to issue history
 */
export const addChange = async (issueId, changeData) => {
  const response = await fetch(`${url}/issue/${issueId}/changes`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(changeData),
  });
  return handleResponse(response);
};

/**
 * Get change history for a issue
 */
export const getChanges = async (issueId, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.skip) queryParams.append("skip", params.skip);
  if (params.limit) queryParams.append("limit", params.limit);

  const response = await fetch(`${url}/issue/${issueId}/changes?${queryParams.toString()}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ==================== SHARING ====================

/**
 * Update sharing settings for a issue
 */
export const updateSharingSettings = async (issueId, settings) => {
  const response = await fetch(`${url}/issue/${issueId}/sharing`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(settings),
  });
  return handleResponse(response);
};

/**
 * Regenerate share token
 */
export const regenerateShareToken = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/regenerate-token`, {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get share URL for a issue
 */
export const getShareUrl = (shareToken) => {
  return `${window.location.origin}/issue/${shareToken}`;
};

/**
 * Copy share URL to clipboard
 */
export const copyShareUrl = async (shareToken) => {
  const shareUrl = getShareUrl(shareToken);
  try {
    await navigator.clipboard.writeText(shareUrl);
    return { success: true, url: shareUrl };
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = shareUrl;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    return { success: true, url: shareUrl };
  }
};

// ==================== COLLABORATOR MANAGEMENT ====================

/**
 * Remove a collaborator from issue
 */
export const removeCollaborator = async (issueId, collaboratorId, isGuest = false) => {
  const response = await fetch(`${url}/issue/${issueId}/collaborator/${collaboratorId}`, {
    method: "DELETE",
    headers: getHeaders(),
    body: JSON.stringify({ isGuest }),
  });
  return handleResponse(response);
};

/**
 * Update collaborator permission
 */
export const updateCollaboratorPermission = async (issueId, collaboratorId, permission, isGuest = false) => {
  const response = await fetch(`${url}/issue/${issueId}/collaborator/${collaboratorId}/permission`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ permission, isGuest }),
  });
  return handleResponse(response);
};

/**
 * Add a collaborator to issue
 */
export const addCollaborator = async (issueId, collaboratorData) => {
  const response = await fetch(`${url}/issue/${issueId}/collaborator`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(collaboratorData),
  });
  return handleResponse(response);
};

/**
 * Get all collaborators for a issue
 */
export const getCollaborators = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/collaborators`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

/**
 * Revoke all guest access and regenerate share token
 */
export const revokeAllGuests = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/revoke-guests`, {
    method: "POST",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ==================== STATUS & PRIORITY ====================

/**
 * Update issue status
 */
// `extra` is a note, or a body to merge - a partner marking an issue
// resolved through their emailed link sends its evidence this way, since
// they have no account to hang a proposal on.
export const updateIssueStatus = async (issueId, status, extra = "") => {
  const rest = typeof extra === "string" ? { notes: extra } : (extra || {});
  // A resolve carries the hours spent (back/utils/issueHours). Every
  // status control in the app comes through here, so they all ask the
  // same way; a caller that already has the hours passes hoursSpent.
  if (String(status) === "resolved" && (rest.hoursSpent === undefined || rest.hoursSpent === null)) {
    const { askHoursSpent } = await import("components/Issue/HoursSpent");
    const hours = await askHoursSpent({ title: "Hours spent on this issue", okText: "Resolve" });
    if (hours === null) throw new Error("Hours spent are required to resolve an issue");
    rest.hoursSpent = hours;
  }
  const response = await fetch(`${url}/issue/${issueId}/status`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ status, ...rest }),
  });
  const body = await handleResponse(response);
  // Every list in the app hears about a finished issue here, once,
  // rather than in each of the five places that change a status.
  //
  // What the SERVER applied is what counts: a partner's "resolved" is a
  // request, and comes back pendingReview with the issue sitting in the
  // Review Center as under_review. Hiding it then would have taken the
  // issue off the list of work still to answer for.
  announceStatus(issueId, body && body.pendingReview ? "under_review" : status);
  return body;
};

/** Tell the lists what an issue's status became (helpers/issueListPrune.js). */
function announceStatus(issueId, applied) {
  try {
    if (isFinishedStatus(applied)) {
      // Two facts, in this order. The SETTLE first, so the row it is on
      // redraws with "Resolved" / "Abandoned" on it for a beat; then the
      // FINISHED mark, which is what takes it off every list once the beat
      // is over. Both only after the server said yes.
      const label = { resolved: "Resolved", abandoned: "Abandoned", rejected: "Rejected" }[String(applied).toLowerCase()] || "Resolved";
      store.dispatch(rowSettled("issues", issueId, label));
      store.dispatch(issueFinished(issueId, applied));
    } else {
      store.dispatch(issueReopened(issueId));
    }
  } catch (e) { /* a list that misses the hint just refetches later */ }
}

/**
 * Update issue priority
 */
export const updateIssuePriority = async (issueId, priority) => {
  const response = await fetch(`${url}/issue/${issueId}/priority`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ priority }),
  });
  return handleResponse(response);
};

/**
 * Set or clear an issue's due date.
 * `dueDate` is a bare "YYYY-MM-DD" day (or null / "" to clear it).
 */
export const updateIssueDueDate = async (issueId, dueDate) => {
  const response = await fetch(`${url}/issue/${issueId}/due-date`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ dueDate: dueDate || null }),
  });
  return handleResponse(response);
};

/**
 * Assign issue to user or guest
 */
export const assignIssue = async (issueId, { userId, guestId, dueDate }) => {
  const response = await fetch(`${url}/issue/${issueId}/assign`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ userId, guestId, dueDate }),
  });
  return handleResponse(response);
};


/**
 * Reopen a resolved/closed/abandoned issue (owner side).
 * Thrown errors carry .status.
 */
export const reopenIssue = async (issueId, note) => {
  const response = await fetch(`${url}/issue/${issueId}/reopen`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ note }),
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    throw error;
  }
  return data;
};

// ==================== TRIAGE & DELTA ====================

/**
 * Element delta for a resolved issue (commit vs its parent).
 * → {commit, summary:{added,removed,modified,unchanged,not_in_model},
 *    elements:[...], beforeFragmentHashes, afterFragmentHashes}
 * Throws with .status 409 when no commit is linked yet.
 */
export const getElementDelta = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/element-delta`, {
    method: "GET",
    headers: getHeaders(),
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

/**
 * Owner-side triage confirm — clears needsTriage, optionally fixing
 * plan tag / priority / due date / location in the same write.
 * body: {ganttItemId?, priority?, dueDate?, location?}
 * Throws with .status 403 when not owner-side.
 */
export const triageIssue = async (issueId, body) => {
  const response = await fetch(`${url}/issue/${issueId}/triage`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(body || {}),
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

/**
 * Issues awaiting owner triage for a project.
 * Wraps GET /issue?projectId=&needsTriage=true → {totalCount, issues}
 */
export const getTriageQueue = async (projectId) => {
  return getIssues({ projectId, needsTriage: "true", limit: 100 });
};

// Every open issue not yet approved to resolve - the Review Center's Approve to
// resolve tab. Resolved and abandoned issues are left out: there is no work
// left to clear.
export const getApprovalQueue = async (projectId) => {
  return getIssues({ projectId, approvedToResolve: false, hideResolved: true, hideAbandoned: true, limit: 500, sort: "displayId", sort_dir: -1 });
};

// ==================== ANALYTICS ====================

/**
 * Get issue analytics for company or project
 */
export const getIssueAnalytics = async (projectId = null) => {
  const queryParams = new URLSearchParams();
  if (projectId) queryParams.append("projectId", projectId);

  const response = await fetch(`${url}/issue/analytics?${queryParams.toString()}`, {
    method: "GET",
    headers: getHeaders(),
  });
  return handleResponse(response);
};

// ==================== UTILITY ====================

/**
 * Check if current user is authenticated
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem(tokenRef);
};

/**
 * Check if current session has guest access
 */
export const hasGuestAccess = () => {
  return !!getGuestToken();
};

/**
 * Get current access level
 */
export const getAccessLevel = () => {
  if (isAuthenticated()) return "authenticated";
  if (hasGuestAccess()) return "guest";
  return "anonymous";
};

/**
 * BCF 2.1 export (.bcfzip) of the issues matching the given filters and
 * trigger the browser download. { projectId?, status?, priority?, search?,
 * ids?, hideAbandoned?, hideResolved? } - with no projectId every issue
 * the caller can see is exported.
 * Resolves { count, filename }.
 */
/**
 * "_SNAG_RFI-7" for a tag filter of "SNAG,RFI-7"; "" with none. The same
 * rule as the server's utils/issuePeopleFilter.fileTagSuffix: letters,
 * digits, - and _ only (a space becomes -), five tags then "+N".
 */
export const exportTagSuffix = (titleTag) => {
  const tags = Array.from(new Set(String(titleTag || "").split(",")
    .map((t) => t.trim().toUpperCase().replace(/\s+/g, "-").replace(/[^A-Za-z0-9_-]+/g, "").replace(/^-+|-+$/g, ""))
    .filter(Boolean)));
  if (!tags.length) return "";
  const shown = tags.slice(0, 5);
  return `_${shown.join("_")}${tags.length > 5 ? `_+${tags.length - 5}` : ""}`;
};

export const exportIssuesBcf = (filters = {}) => exportIssues("bcf", filters);
export const exportIssuesPdf = (filters = {}) => exportIssues("pdf", filters);
export const exportIssuesExcel = (filters = {}) => exportIssues("excel", filters);

/**
 * Shared exporter: format "bcf" (.bcfzip, BCF 2.1), "excel", "csv" or "pdf" (register
 * table + per-issue details + comments; a single id renders the
 * one-issue layout). Same filters either way.
 */
// WHAT THIS PERSON MAY PUT IN AN EXPORT, for the dialog (owner, 2026-10-22).
// Resolves { hours, prices }; a failure answers neither rather than both.
export const getExportOptions = async (projectId) => {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  const response = await fetch(`${url}/issue/export/options${qs}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

export const exportIssues = async (format, { projectId, status, priority, search, ids, hideAbandoned, hideResolved, hideUnderReview, hidePending, approvedToResolve, includeHours, includeEstimates, includePrices, createdFrom, createdTo, dueFrom, dueTo, titleTag, createdBy, updatedBy, assignee, ganttItemId, dueToday, createdToday, overdue, contractor, areaSignOff, areaStage, areaSignOffAsOf, ownerSignOff, level, area, grid, system, discipline } = {}) => {
  const qs = new URLSearchParams();
  if (projectId) qs.append("projectId", projectId);
  if (status && status !== "all") qs.append("status", Array.isArray(status) ? status.join(",") : status);
  if (priority && priority !== "all") qs.append("priority", Array.isArray(priority) ? priority.join(",") : priority);
  if (search && String(search).trim()) qs.append("search", String(search).trim());
  if (ids && ids.length) qs.append("ids", ids.join(","));
  // The list's hide checkboxes, so an export matches what was on screen.
  if (hideAbandoned) qs.append("hideAbandoned", "true");
  if (hideResolved) qs.append("hideResolved", "true");
  if (hideUnderReview) qs.append("hideUnderReview", "true");
  if (hidePending) qs.append("hidePending", "true");
  if (approvedToResolve !== undefined) qs.append("approvedToResolve", String(approvedToResolve));
  // The dialog's answer. Sent only when it was asked, so an export triggered
  // from anywhere else behaves exactly as it did.
  if (includeHours !== undefined) qs.append("includeHours", includeHours ? "true" : "false");
  if (includeEstimates !== undefined) qs.append("includeEstimates", includeEstimates ? "true" : "false");
  if (includePrices !== undefined) qs.append("includePrices", includePrices ? "true" : "false");
  // The list's date ranges, so the zip matches the rows on screen.
  if (createdFrom) qs.append("createdFrom", createdFrom);
  if (createdTo) qs.append("createdTo", createdTo);
  if (dueFrom) qs.append("dueFrom", dueFrom);
  if (dueTo) qs.append("dueTo", dueTo);
  if (createdFrom || createdTo) qs.append("tzOffset", String(new Date().getTimezoneOffset()));
  // Printing one tag's worth of issues is what the tag is FOR, so the
  // export has to accept it alongside the list's other filters.
  if (titleTag) qs.append("titleTag", titleTag);
  if (createdBy) qs.append("createdBy", createdBy);
  if (updatedBy) qs.append("updatedBy", updatedBy);
  // The toolbar's own filters. Without these the export answered a
  // different question from the screen: 4 rows visible, the project
  // exported. `tzOffset` goes whenever a day-relative button is on,
  // because "today" is the reader's day, not the server's.
  if (assignee) qs.append("assignee", assignee);
  if (ganttItemId) qs.append("ganttItemId", ganttItemId);
  if (dueToday) qs.append("dueToday", "true");
  if (createdToday) qs.append("createdToday", "true");
  if (overdue) qs.append("overdue", "true");
  if ((dueToday || createdToday || overdue) && !qs.has("tzOffset")) qs.append("tzOffset", String(new Date().getTimezoneOffset()));
  // ONE CONTRACTOR'S WORK (owner, 2026-10-06). The document then contains
  // the issues that contractor raised, holds, resolved or logged hours on, their hours and
  // their rates only - and says so on its first page, with how many issues
  // it left out.
  if (contractor && contractor !== "all") qs.append("contractor", contractor);
  appendAreaSignOff(qs, { projectId, areaSignOff, areaStage, areaSignOffAsOf, ownerSignOff });
  // Level / area / grid / system / discipline (owner, 2026-10-17), each "A,B".
  for (const [k, v] of Object.entries({ level, area, grid, system, discipline })) if (v) qs.append(k, v);
  const route = format === "pdf" ? "pdf-export" : format === "excel" ? "excel-export" : format === "csv" ? "csv-export" : "bcf-export";
  const response = await fetch(`${url}/issue/${route}${qs.toString() ? `?${qs}` : ""}`, { method: "GET", headers: authOnlyHeaders() });
  if (!response.ok) {
    let msg = `HTTP ${response.status}`;
    try { msg = (await response.json()).err || msg; } catch (e) { /* not json */ }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const cd = response.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  // The server names the file (with the filter's tags on the end since
  // 2026-10-15); this fallback only runs when the header is unreadable, and
  // carries the same tag suffix so the two never name one export differently.
  const filename = m ? m[1] : `issues-${new Date().toISOString().slice(0, 10)}${exportTagSuffix(titleTag)}.${format === "pdf" ? "pdf" : format === "excel" ? "xlsx" : format === "csv" ? "csv" : "bcfzip"}`;
  const count = Number(response.headers.get("X-Issue-Count") || 0);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return { count, filename };
};

/**
 * The partner companies on a project, for the export's contractor picker.
 * Only the partners actually on that project - never every company - and a
 * partner account sees only itself.
 */
export const getExportContractors = async (projectId) => {
  const response = await fetch(`${url}/issue/export-contractors?projectId=${encodeURIComponent(projectId)}`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** 3D sources selectable for the issue: project model versions (revisions) + issue exports → { timelines, issues, current } */
export const getIssueModelSources = async (issueId) => {
  const response = await fetch(`${url}/issue/${issueId}/model-sources`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/** Point the issue's 3D pane at { timelineId } | { fromIssueId } | { commitId } | { clear: true } → { sourceModel, sourceType } */
export const setIssueSourceModel = async (issueId, body) => {
  const response = await fetch(`${url}/issue/${issueId}/source-model`, { method: "PUT", headers: getHeaders(), body: JSON.stringify(body || {}) });
  return handleResponse(response);
};

/**
 * Pin (or clear) the camera the issue's 3D markup was drawn at.
 * body: { camera, fov, aspect, screenshotUrl, screenshotVersion,
 *         hiddenElements, isolatedElements, manifestPath, commitId }
 *   or { clear: true }
 */
export const setIssueMarkupView = async (issueId, body) => {
  const response = await fetch(`${url}/issue/${issueId}/markup-view`, { method: "PUT", headers: getHeaders(), body: JSON.stringify(body || {}) });
  return handleResponse(response);
};

/** Create page: 3D sources of a project (model versions + issue exports, with zipInfo) → { timelines, issues } */
export const getProjectModelSources = async (projectId) => {
  const response = await fetch(`${url}/issue/project/${projectId}/model-sources?full=1`, { method: "GET", headers: getHeaders() });
  return handleResponse(response);
};

/**
 * Upload one screenshot and get a server path back (/uploads/issue-screenshots/…).
 * Used before an issue exists - the create page does it for a pasted image, and
 * the revision viewer does it for a capture of the 3D canvas.
 * → { url, bytes }
 */
export const uploadStandaloneScreenshot = async (file) => {
  const fd = new FormData();
  fd.append("screenshot", file, file.name || "screenshot.png");
  const response = await fetch(`${url}/issue/proposals/screenshot`, {
    method: "POST", headers: authOnlyHeaders(), body: fd,
  });
  return handleResponse(response);
};

/**
 * Propose a resolution - the web equivalent of the plugin's "Resolve issue".
 * body: { commitId, resolvedElements, afterFragmentHashes, notes,
 *         submissionChannel, elementPickMode, screenshotUrls, evidence }
 * → { proposal, autoApproved, status }. autoApproved is true when the project
 * does not require proposal approval, in which case the issue is resolved on
 * the spot; otherwise it lands in the owner's Review Center.
 */
export const submitIssueProposal = async (issueId, body) => {
  const response = await fetch(`${url}/issue/${issueId}/proposals`, {
    method: "POST", headers: getHeaders(), body: JSON.stringify(body || {}),
  });
  return handleResponse(response);
};

// EXTRA SCOPE: "not for this one". The issue stays marked as extra scope -
// somebody has been told about it - and the reason is kept, because "we
// decided no drawing was needed" is the sentence that gets asked about later.
export const declineExtraScopeDrawing = async (issueId, { reason = "" } = {}) => {
  const response = await fetch(`${url}/issue/${issueId}/extra-scope/decline`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ reason }),
  });
  return handleResponse(response);
};

// APPROVED TO RESOLVE - clearance to work on an issue (owner 2026-10-26).
// ONE CALL for the single toggle and the issue manager's bulk action, so the
// two can never drift apart. Only the reviewer side may change it; the server
// checks every project in the set, not just the first.
export const setIssuesApprovedToResolve = async (ids, approved) => {
  const response = await fetch(`${url}/issue/approved-to-resolve`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ ids: Array.isArray(ids) ? ids : [ids], approved }),
  });
  return handleResponse(response);
};

// Back to vetting: sets needsTriage, clears the approval, and withdraws any
// pending resolution proposal so the triage and proposals queues cannot
// disagree about the same issue.
export const sendIssuesToTriage = async (ids, reason) => {
  const response = await fetch(`${url}/issue/send-to-triage`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ ids: Array.isArray(ids) ? ids : [ids], reason: reason || "" }),
  });
  return handleResponse(response);
};
